import { useState, useCallback, useRef, useEffect } from 'react';
import { generatePath } from 'react-router-dom';
import { pepApiRequest, RequestMethod } from 'src/services';
import { API_POST_SOLUTION_EVENTS_BATCH } from 'src/pages/Solutions/constants';
import { parseDtdlAndTwin } from './utils/dtdlParser';
import PropertySpecRow from './components/PropertySpecRow';
import SimulationControls from './components/SimulationControls';
import SimulationPreview from './components/SimulationPreview';
import InfoTip from './components/InfoTip';
import {
  generateBatch,
  generateRow,
  createSimulatorState,
  resetSimulatorState,
  toCSV,
  toAvro,
  toParquet,
  downloadFile,
  downloadBinaryFile,
} from './utils/simulator';
import type { SimulatorState } from './utils/simulator';
import type {
  TwinDataSpec,
  SimulationConfig,
  SimulationRow,
  PropertySpec,
} from './types/spec';
import './DataSimulation.css';

interface Props {
  /** Raw DTDL model object from API_GET_ASSET_DIGITALTWIN_MODEL (result[0]) */
  dtdlModel: Record<string, unknown> | null;
  /** Raw twin instance object from API_GET_ASSET_DIGITALTWIN_PROPERTIES */
  twinInstance: Record<string, unknown> | null;
  /** Twin ID for naming exported files */
  twinId: string;
  /** Solution ID for event publishing */
  solutionId: string;
  /** Site ID for event publishing */
  siteId: string;
}

const MIN_SEND_INTERVAL_MS = 2000;
const MAX_BATCH_PAYLOAD_BYTES = 0.75 * 1024 * 1024;

const encoder = new TextEncoder();

function getBatchPayloadSizeBytes(
  events: Array<Record<string, unknown>>,
  twinId: string
): number {
  return encoder.encode(JSON.stringify({ events, twinId })).length;
}

function chunkEventsBySize(
  events: Array<Record<string, unknown>>,
  maxBytes: number,
  twinId: string
): Array<Array<Record<string, unknown>>> {
  const chunks: Array<Array<Record<string, unknown>>> = [];
  let currentChunk: Array<Record<string, unknown>> = [];

  events.forEach((event) => {
    const singleEventSize = getBatchPayloadSizeBytes([event], twinId);
    if (singleEventSize > maxBytes) {
      throw new Error(
        `A single event exceeds the 750KB payload limit (${singleEventSize} bytes).`
      );
    }

    const nextChunk = [...currentChunk, event];
    const nextChunkSize = getBatchPayloadSizeBytes(nextChunk, twinId);
    if (nextChunkSize <= maxBytes) {
      currentChunk = nextChunk;
      return;
    }

    if (currentChunk.length > 0) {
      chunks.push(currentChunk);
    }
    currentChunk = [event];
  });

  if (currentChunk.length > 0) {
    chunks.push(currentChunk);
  }

  return chunks;
}

const DEFAULT_CONFIG: SimulationConfig = {
  sampleCount: 100,
  intervalMs: 2000,
  mode: 'batch',
};

export default function DataSimulation({
  dtdlModel,
  twinInstance,
  twinId,
  solutionId,
  siteId,
}: Props) {
  const [spec, setSpec] = useState<TwinDataSpec | null>(null);
  const [config, setConfig] = useState<SimulationConfig>({ ...DEFAULT_CONFIG });
  const [status, setStatus] = useState<
    'idle' | 'running' | 'paused' | 'completed'
  >('idle');
  const [data, setData] = useState<SimulationRow[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const simStateRef = useRef<SimulatorState | null>(null);
  const sendChainRef = useRef<Promise<void>>(Promise.resolve());
  const sendSessionRef = useRef(0);
  const nextSendAllowedAtRef = useRef(0);
  // Keep a ref to the latest spec so the streaming interval can always access it
  const specRef = useRef<TwinDataSpec | null>(null);
  specRef.current = spec;

  const stopInterval = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const wait = useCallback((ms: number) => {
    return new Promise<void>((resolve) => {
      setTimeout(resolve, ms);
    });
  }, []);

  const mapRowsToEvents = useCallback(
    (rows: SimulationRow[]): Array<Record<string, unknown>> => {
      return rows.map((row) => {
        return { ...row.values } as Record<string, unknown>;
      });
    },
    []
  );

  const postEventChunk = useCallback(
    async (events: Array<Record<string, unknown>>, sessionId: number) => {
      if (sessionId !== sendSessionRef.current) return;

      const waitMs = Math.max(0, nextSendAllowedAtRef.current - Date.now());
      if (waitMs > 0) {
        await wait(waitMs);
      }

      if (sessionId !== sendSessionRef.current) return;

      const url = generatePath(API_POST_SOLUTION_EVENTS_BATCH, {
        solutionId,
        siteId,
      });

      await pepApiRequest({
        method: RequestMethod.Post,
        url,
        params: { events, twinId },
      });

      nextSendAllowedAtRef.current = Date.now() + MIN_SEND_INTERVAL_MS;
    },
    [siteId, solutionId, twinId, wait]
  );

  const enqueueRowsForPublishing = useCallback(
    (rows: SimulationRow[]) => {
      const events = mapRowsToEvents(rows);
      const eventChunks = chunkEventsBySize(
        events,
        MAX_BATCH_PAYLOAD_BYTES,
        twinId
      );
      const sessionId = sendSessionRef.current;

      sendChainRef.current = eventChunks.reduce(
        (chain, chunk) => chain.then(() => postEventChunk(chunk, sessionId)),
        sendChainRef.current
      );

      sendChainRef.current = sendChainRef.current.catch((e) => {
        const message =
          e instanceof Error
            ? e.message
            : 'Failed to publish simulation events.';
        setError(message);
      });
    },
    [mapRowsToEvents, postEventChunk]
  );

  // Auto-parse the spec whenever the model or twin instance changes
  useEffect(() => {
    if (!dtdlModel) {
      setSpec(null);
      setWarnings([]);
      setError(null);
      return;
    }
    try {
      const dtdlText = JSON.stringify(dtdlModel);
      const twinText = twinInstance ? JSON.stringify(twinInstance) : '{}';
      const result = parseDtdlAndTwin(dtdlText, twinText);
      setSpec(result.spec);
      setWarnings(result.warnings);
      setError(null);
      // Reset simulation when asset/model changes
      setData([]);
      setStatus('idle');
      stopInterval();
      sendSessionRef.current += 1;
      simStateRef.current = null;
    } catch (e) {
      setError((e as Error).message);
      setSpec(null);
    }
  }, [dtdlModel, twinInstance, stopInterval]);

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      stopInterval();
      sendSessionRef.current += 1;
    };
  }, [stopInterval]);

  const handlePropertyChange = useCallback(
    (index: number, updated: PropertySpec) => {
      setSpec((prev) => {
        if (!prev) return prev;
        const props = [...prev.properties];
        props[index] = updated;
        return { ...prev, properties: props };
      });
      // Rebuild simulator state when spec edited
      simStateRef.current = null;
    },
    []
  );

  const handleStart = useCallback(() => {
    const currentSpec = specRef.current;
    if (!currentSpec) return;
    if (!solutionId || !siteId) {
      setError('Solution and site are required to publish simulation events.');
      return;
    }

    const includedSpecs = currentSpec.properties.filter((p) => p.included);
    if (includedSpecs.length === 0) return;

    if (!simStateRef.current) {
      simStateRef.current = createSimulatorState(includedSpecs);
    }

    if (config.mode === 'batch') {
      setStatus('running');
      const rows = generateBatch(
        includedSpecs,
        config.sampleCount,
        simStateRef.current
      );
      setData((prev) => [...prev, ...rows]);
      enqueueRowsForPublishing(rows);
      setStatus('completed');
    } else {
      setStatus('running');
      const streamIntervalMs = Math.max(
        config.intervalMs,
        MIN_SEND_INTERVAL_MS
      );
      intervalRef.current = setInterval(() => {
        const latestSpec = specRef.current;
        if (!latestSpec || !simStateRef.current) return;
        const specs = latestSpec.properties.filter((p) => p.included);
        if (specs.length === 0) return;
        const row = generateRow(specs, simStateRef.current);
        setData((prev) => [...prev, row]);
        enqueueRowsForPublishing([row]);
      }, streamIntervalMs);
    }
  }, [config, enqueueRowsForPublishing, siteId, solutionId]);

  const handlePause = useCallback(() => {
    stopInterval();
    sendSessionRef.current += 1;
    setStatus('paused');
  }, [stopInterval]);

  const handleStop = useCallback(() => {
    stopInterval();
    sendSessionRef.current += 1;
    if (simStateRef.current) {
      resetSimulatorState(simStateRef.current);
    }
    setStatus('idle');
  }, [stopInterval]);

  const handleExportJSON = useCallback(() => {
    if (data.length === 0) return;
    const json = JSON.stringify(data, null, 2);
    const filename = `sim_${spec?.twinId || twinId}_${Date.now()}.json`;
    downloadFile(json, filename, 'application/json');
  }, [data, spec, twinId]);

  const handleExportCSV = useCallback(() => {
    if (!spec || data.length === 0) return;
    const csv = toCSV(data, spec.properties);
    const filename = `sim_${spec.twinId || twinId}_${Date.now()}.csv`;
    downloadFile(csv, filename, 'text/csv');
  }, [data, spec, twinId]);

  const handleExportAvro = useCallback(() => {
    if (!spec || data.length === 0) return;
    const avro = toAvro(data, spec.properties);
    const filename = `sim_${spec.twinId || twinId}_${Date.now()}.avro`;
    downloadBinaryFile(avro, filename, 'application/octet-stream');
  }, [data, spec, twinId]);

  const handleExportParquet = useCallback(async () => {
    if (!spec || data.length === 0) return;
    const parquet = await toParquet(data, spec.properties);
    const filename = `sim_${spec.twinId || twinId}_${Date.now()}.parquet`;
    downloadBinaryFile(parquet, filename, 'application/octet-stream');
  }, [data, spec, twinId]);

  if (!dtdlModel && !twinInstance) {
    return (
      <p className="ds-empty-hint">
        Select an asset node from the tree to load simulation configuration.
      </p>
    );
  }

  return (
    <div className="ds-container">
      {error && <div className="ds-error">{error}</div>}

      {warnings.length > 0 && (
        <div className="ds-warnings">
          {warnings.map((w, i) => (
            <p key={i}>{w}</p>
          ))}
        </div>
      )}

      {/* ===== Property Specification Table — always visible once a model is loaded ===== */}
      <section className="ds-spec">
        <div className="ds-spec-header">
          <h3>Property Specification</h3>
          {spec && (
            <div className="ds-meta">
              <span>
                <strong>Model:</strong> {spec.modelDisplayName}{' '}
                <code>{spec.modelId}</code>
              </span>
              {spec.twinId && (
                <span>
                  <strong>Twin:</strong> <code>{spec.twinId}</code>
                </span>
              )}
              <span>
                <strong>Properties:</strong>{' '}
                {spec.properties.filter((p) => p.included).length} /{' '}
                {spec.properties.length} included
              </span>
            </div>
          )}
        </div>

        <div className="ds-table-wrap">
          <table className="ds-table">
            <thead>
              <tr>
                <th>
                  <InfoTip text="Toggle whether this property is included in the simulation output.">
                    Inc.
                  </InfoTip>
                </th>
                <th>
                  <InfoTip text="The property name from the DTDL model. Display name shown first; programmatic name below if different.">
                    Property
                  </InfoTip>
                </th>
                <th>
                  <InfoTip text="DTDL content type — Telemetry (time-series) or Property (reported/desired state).">
                    DTDL Type
                  </InfoTip>
                </th>
                <th>
                  <InfoTip text="The DTDL schema type (e.g. double, integer, string, Enum, boolean).">
                    Schema
                  </InfoTip>
                </th>
                <th>
                  <InfoTip text="The current value of this property as returned by the digital twin API.">
                    Twin Value
                  </InfoTip>
                </th>
                <th>
                  <InfoTip text="Data classification used for simulation sampling: Numerical Continuous, Discrete, Categorical, Boolean, DateTime, or Text.">
                    Data Type (ML)
                  </InfoTip>
                </th>
                <th>
                  <InfoTip text="Configure value range, distribution, and generation parameters for simulation.">
                    Range / Constraints
                  </InfoTip>
                </th>
                <th>
                  <InfoTip text="When enabled, the simulator may randomly produce null values for this property.">
                    Nullable
                  </InfoTip>
                </th>
                <th>
                  <InfoTip text="Configure anomaly injection — control how often abnormal values appear and their deviation from normal.">
                    Anomaly
                  </InfoTip>
                </th>
              </tr>
            </thead>
            <tbody>
              {spec && spec.properties.length > 0 ? (
                spec.properties.map((p, i) => (
                  <PropertySpecRow
                    key={p.name}
                    property={p}
                    onChange={(updated) => handlePropertyChange(i, updated)}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="ds-empty-row">
                    {error
                      ? 'Could not parse model — check the error above.'
                      : 'No simulation properties found in this asset model.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ===== Simulation Controls — always visible once a model is loaded ===== */}
      <SimulationControls
        config={config}
        status={status}
        dataCount={data.length}
        disabled={
          !spec ||
          spec.properties.filter((p) => p.included).length === 0 ||
          !solutionId ||
          !siteId
        }
        onConfigChange={setConfig}
        onStart={handleStart}
        onPause={handlePause}
        onStop={handleStop}
        onExportJSON={handleExportJSON}
        onExportCSV={handleExportCSV}
        onExportAvro={handleExportAvro}
        onExportParquet={handleExportParquet}
      />

      {/* ===== Simulation Data Preview ===== */}
      <SimulationPreview rows={data} specs={spec?.properties ?? []} />
    </div>
  );
}
